import React, { useContext, useEffect, useRef, useState } from "react"
import { View, StyleSheet, Image } from "react-native"
import Animated, {
    Extrapolation,
    interpolate,
    useAnimatedScrollHandler,
    useAnimatedStyle,
    useSharedValue,
} from "react-native-reanimated"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
    BAND_MAX,
    BAND_MIN,
    BAND_COLLAPSE_RANGE,
    BAND_PARALLAX_RISE,
} from "@common/components/funnel/FunnelQuestionScreen"
import ClassChipGridM from "@common/components/funnel/ClassChipGridM"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { useRoute } from "@react-navigation/native"
import { apiRequest } from "@data/redux/actions/api"
import logo from "@assets/logo-blue.png"
import classeIllustration from "@assets/summer/funnel/classe.png"
import BrevetFunnelHeader from "../brevet/components/BrevetFunnelHeader"
import { useSummerFunnel } from "./context/SummerFunnelContext"
import { useSummerFunnelGuard } from "./hooks/useSummerFunnelGuard"
import { summerFunnelService } from "./services/summerFunnelService"
import { NEXT_CLASS, normaliseClass } from "./constants/summerContent"

// Classes proposées (alignées sur User::$studentClasses backend).
const STUDENT_CLASSES = [
    "CE2", "CM1", "CM2", "6ème", "5ème", "4ème", "3ème", "2nde", "1ère", "Tle", "Autre",
]

const FUNNEL_TOTAL_STEPS = 7
const CONTENT_MAX = 460

// Split desktop : aligné sur FunnelQuestionScreen (objectif/niveau) pour
// l'homogénéité — illustration à gauche, contenu à droite. En-deçà, layout
// mobile empilé avec parallaxe du bandeau.
const WIDE_BREAKPOINT = 900
const WIDE_CONTENT_MAX = 480

/**
 * Étape 1 du funnel Cours d'été — UN SEUL écran qui regroupe les DEUX questions
 * de classe : la classe de cette année (2025-2026) ET la classe à la rentrée
 * (2026-2027, pré-remplie par dérivation, corrigeable). Les classes sont
 * nombreuses (CE2…Terminale + Autre) → on les rend en grille de pastilles
 * compactes (ClassChipGridM) plutôt qu'en cartes pleine largeur.
 *
 * MÊME langage visuel que les écrans de question (FunnelQuestionScreen) :
 *  - ÉTROIT (mobile) : bandeau pastel pinné en haut, illustration en fond qui
 *    REMONTE et se fait clipper au scroll (parallaxe Preply) ; le titre fait
 *    partie du contenu qui défile, juste sous le bandeau. CTA collant en bas.
 *  - LARGE (desktop) : split deux colonnes — illustration pastel à gauche, titre
 *    + les deux grilles de chips + CTA à droite (même homogénéité que les autres
 *    écrans illustrés).
 */
const SummerClassScreen = (props) => {
    const { navigation, reduxFunnelStateId, reduxInitUser } = props
    const route = useRoute()
    const dimensions = useContext(DimensionsContext)
    const windowWidth = dimensions?.width || 400
    const isWide = windowWidth >= WIDE_BREAKPOINT
    const { state, update, reset } = useSummerFunnel()
    // Refresh-safe / URL-partageable : maintient `?user=` dans l'URL de l'étape.
    // L'écran de départ n'a pas de redirection « retour au début » (isStart),
    // mais ré-hydrate quand même via summer/resume si un `user` arrive sur un
    // contexte froid (URL d'entrée ouverte dans un contexte neuf).
    // `nextParams` propage `?user=` sur chaque navigation.navigate aval pour
    // garder l'URL partageable.
    const { nextParams } = useSummerFunnelGuard({ isStart: true })
    const amplitudeTrackedRef = useRef(false)

    const [studentClass, setStudentClass] = useState(state.studentClass || null)
    const [classRentree, setClassRentree] = useState(state.studentClassRentree || null)

    // Parallaxe du bandeau (étroit) — MÊME mécanique que FunnelQuestionScreen :
    // hauteur du bandeau et translateY de l'image = fonctions pures de scrollY.
    const scrollY = useSharedValue(0)
    const scrollHandler = useAnimatedScrollHandler({
        onScroll: (event) => {
            scrollY.value = event.contentOffset.y
        },
    })
    const bandAnimatedStyle = useAnimatedStyle(() => {
        const height = interpolate(
            scrollY.value,
            [0, BAND_COLLAPSE_RANGE],
            [BAND_MAX, BAND_MIN],
            Extrapolation.CLAMP
        )
        return { height }
    })
    const imageAnimatedStyle = useAnimatedStyle(() => {
        const translateY = interpolate(
            scrollY.value,
            [0, BAND_COLLAPSE_RANGE],
            [0, -BAND_PARALLAX_RISE],
            Extrapolation.CLAMP
        )
        const opacity = interpolate(
            scrollY.value,
            [0, BAND_COLLAPSE_RANGE * 0.85],
            [1, 0],
            Extrapolation.CLAMP
        )
        return { transform: [{ translateY }], opacity }
    })

    // Hydratation depuis query params / HubSpot (1x au mount).
    useEffect(() => {
        const params = route.params || {}
        const submissionGUID = params.submissionGUID || params.submission_guid || null

        const guidMismatch =
            submissionGUID && state.submissionGUID && state.submissionGUID !== submissionGUID
        if (guidMismatch) reset()

        if (submissionGUID) {
            update({ submissionGUID })
            props.apiRequest(summerFunnelService.initFromHubSpot, {}, { submission_guid: submissionGUID })
        } else {
            const klass = normaliseClass(params.class) || state.studentClass
            const patch = {}
            if (params.firstname && !state.studentFirstname) patch.studentFirstname = params.firstname
            if (klass && !state.studentClass) patch.studentClass = klass
            if (params.email && !state.parentEmail) patch.parentEmail = params.email
            // Hydrate local state too (mirror of the reduxInitUser effect) so the
            // card shows selected and "Continuer" works without a manual click.
            if (klass && !studentClass) setStudentClass(klass)
            if (klass && !classRentree) {
                const rentree = NEXT_CLASS[klass] || null
                if (rentree) {
                    setClassRentree(rentree)
                    if (!state.studentClassRentree) patch.studentClassRentree = rentree
                }
            }
            if (Object.keys(patch).length) update(patch)

            const effectiveEmail = params.email || state.parentEmail
            if (!state.funnelStateId && !reduxFunnelStateId && effectiveEmail) {
                props.apiRequest(summerFunnelService.startFunnel, {}, {
                    firstname: params.firstname || state.studentFirstname || "",
                    student_class: klass || "3ème",
                    parent_email: effectiveEmail,
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Aligne le funnel_state_id (success start/init) dans le context.
    useEffect(() => {
        if (reduxFunnelStateId && reduxFunnelStateId !== state.funnelStateId) {
            update({ funnelStateId: reduxFunnelStateId })
        }
    }, [reduxFunnelStateId, state.funnelStateId, update])

    // Pousse le user pré-rempli (/init) dans le Context (state.* gagne si déjà saisi).
    useEffect(() => {
        if (!reduxInitUser) return
        const patch = {}
        if (reduxInitUser.firstname && !state.studentFirstname) patch.studentFirstname = reduxInitUser.firstname
        if (reduxInitUser.email && !state.parentEmail) patch.parentEmail = reduxInitUser.email
        if (reduxInitUser.student_class && !studentClass) {
            patch.studentClass = reduxInitUser.student_class
            setStudentClass(reduxInitUser.student_class)
        }
        if (reduxInitUser.student_class_rentree && !classRentree) {
            patch.studentClassRentree = reduxInitUser.student_class_rentree
            setClassRentree(reduxInitUser.student_class_rentree)
        }
        if (Object.keys(patch).length) update(patch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduxInitUser])

    // Amplitude — funnel started (1x dès qu'on a un funnel_state_id).
    useEffect(() => {
        if (!amplitudeTrackedRef.current && state.funnelStateId) {
            amplitudeTrackedRef.current = true
            amplitude.track("summer_funnel_started", { funnel_state_id: state.funnelStateId })
        }
    }, [state.funnelStateId])

    const onSelectClass = (c) => {
        setStudentClass(c)
        // Pré-remplit la rentrée par dérivation si le parent ne l'a pas corrigée.
        if (!classRentree || classRentree === NEXT_CLASS[studentClass]) {
            setClassRentree(NEXT_CLASS[c] || null)
        }
    }

    const persistAndContinue = () => {
        const rentree = classRentree || NEXT_CLASS[studentClass] || null
        update({ studentClass, studentClassRentree: rentree })
        const stateId = state.funnelStateId || reduxFunnelStateId
        if (stateId) {
            props.apiRequest(
                summerFunnelService.updateAnswers,
                { stateId },
                { studentClass, studentClassRentree: rentree }
            )
        }
        amplitude.track("summer_funnel_class_completed", { funnel_state_id: stateId })
        navigation.navigate("summer/diagnostic", nextParams)
    }

    const firstname = state.studentFirstname || route.params?.firstname || "votre enfant"

    // Années scolaires pour les libellés (édition été de l'année en cours).
    const editionYear = new Date().getFullYear()
    const pastSchoolYear = `${editionYear - 1}-${editionYear}`
    const rentreeSchoolYear = `${editionYear}-${editionYear + 1}`

    const classOptions = STUDENT_CLASSES.map((c) => ({ id: c, label: c }))

    // CTA actif quand les DEUX classes sont renseignées (la rentrée est
    // pré-remplie par dérivation dès qu'une classe est choisie).
    const canContinue = !!studentClass && !!classRentree

    // Titre seul (réutilisé étroit/large).
    const titleBlock = (
        <TextM wrap style={styles.title}>
            La classe de {firstname}
        </TextM>
    )

    // Les deux grilles de chips (cette année + rentrée), réutilisées étroit/large.
    const chipsBlock = (
        <>
            <TextM wrap style={styles.sectionLabel}>
                Cette année ({pastSchoolYear})
            </TextM>
            <ClassChipGridM
                options={classOptions}
                selectedIds={studentClass ? [studentClass] : []}
                onToggle={onSelectClass}
            />

            <TextM wrap style={styles.sectionLabelSpaced}>
                À la rentrée ({rentreeSchoolYear})
            </TextM>
            <ClassChipGridM
                options={classOptions}
                selectedIds={classRentree ? [classRentree] : []}
                onToggle={(c) => setClassRentree(c)}
            />
        </>
    )

    const ctaFooter = (
        <View style={styles.footer}>
            <FilledButtonM
                label="Continuer"
                onPress={persistAndContinue}
                disabled={!canContinue}
                color={canContinue ? theme.colors.primary : theme.colors.lightMore}
                style={{ width: "100%", marginBottom: 0 }}
            />
        </View>
    )

    // ===== LARGE (desktop) : split deux colonnes (homogène avec objectif/niveau) =====
    if (isWide) {
        return (
            <View style={styles.wideRoot}>
                {/* Panneau pastel gauche (illustration + logo). */}
                <View
                    style={[
                        styles.widePanel,
                        { backgroundColor: theme.colors.primaryLighter },
                    ]}
                >
                    <Image
                        source={classeIllustration}
                        style={styles.wideIllustration}
                        resizeMode="contain"
                    />
                    <Image source={logo} style={styles.wideLogo} resizeMode="contain" />
                </View>

                {/* Colonne blanche droite (progress + titre + 2 grilles + CTA). */}
                <View style={styles.wideContentCol}>
                    <View style={styles.wideContentInner}>
                        <BrevetFunnelHeader
                            step={1}
                            total={FUNNEL_TOTAL_STEPS}
                            hideBack
                            hideLogo
                        />
                        <Animated.ScrollView
                            contentContainerStyle={styles.wideScrollContent}
                            showsVerticalScrollIndicator={false}
                        >
                            {titleBlock}
                            <View style={{ marginTop: 8 }}>{chipsBlock}</View>
                        </Animated.ScrollView>
                        {ctaFooter}
                    </View>
                </View>
            </View>
        )
    }

    // ===== ÉTROIT (mobile) : bandeau pastel pinné + parallaxe image =====
    const containerWidth = Math.min(windowWidth, CONTENT_MAX)
    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                {/* Progression épinglée tout en haut. */}
                <View style={styles.pinnedHeader}>
                    <BrevetFunnelHeader step={1} total={FUNNEL_TOTAL_STEPS} hideBack hideLogo />
                </View>

                <View style={styles.stage}>
                    {/* Bandeau pastel pinné, image clippée qui remonte au scroll. */}
                    <Animated.View
                        style={[styles.bgBand, bandAnimatedStyle]}
                        pointerEvents="none"
                    >
                        <Animated.Image
                            source={classeIllustration}
                            style={[styles.illustration, imageAnimatedStyle]}
                            resizeMode="contain"
                        />
                    </Animated.View>

                    <Animated.ScrollView
                        style={styles.scroll}
                        contentContainerStyle={styles.content}
                        showsVerticalScrollIndicator={false}
                        scrollEventThrottle={16}
                        onScroll={scrollHandler}
                    >
                        <View style={styles.scrollSpacer} pointerEvents="none" />
                        <View style={styles.sheet}>
                            <View style={styles.scrollTitle}>{titleBlock}</View>
                            {chipsBlock}
                        </View>
                    </Animated.ScrollView>
                </View>

                {ctaFooter}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
    },
    frame: { flex: 1, backgroundColor: theme.colors.white },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 6,
    },
    sectionLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primary,
        marginBottom: 10,
    },
    // Variante espacée (2ᵉ question) — TextM ne supporte qu'un style OBJET (il
    // spread props.style dans un objet), donc pas de tableau de styles ici.
    sectionLabelSpaced: {
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primary,
        marginTop: 24,
        marginBottom: 10,
    },
    footer: {
        paddingHorizontal: 20,
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },

    // --- narrow (mobile) : bandeau pinné + parallaxe ---
    pinnedHeader: {
        backgroundColor: theme.colors.white,
        zIndex: 3,
        paddingHorizontal: 20,
    },
    stage: { flex: 1, position: "relative" },
    bgBand: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: BAND_MAX,
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        backgroundColor: theme.colors.primaryLighter,
        zIndex: 0,
    },
    illustration: { width: 156, height: 156 },
    scroll: { flex: 1, zIndex: 1 },
    scrollSpacer: { height: BAND_MAX, backgroundColor: "transparent" },
    sheet: {
        backgroundColor: theme.colors.white,
        paddingHorizontal: 20,
        paddingTop: 16,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
    },
    scrollTitle: { paddingBottom: 10 },
    content: { paddingBottom: 28 },

    // --- wide (desktop split) ---
    wideRoot: {
        flex: 1,
        flexDirection: "row",
        backgroundColor: theme.colors.white,
    },
    widePanel: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
    },
    wideIllustration: {
        width: "60%",
        maxWidth: 380,
        height: "55%",
    },
    wideLogo: {
        position: "absolute",
        bottom: 24,
        left: 24,
        width: 96,
        height: 28,
    },
    wideContentCol: {
        flex: 1,
        backgroundColor: theme.colors.white,
        alignItems: "center",
    },
    // Colonne capée + centrée → padding confortable à 1440 (titre pas collé).
    wideContentInner: {
        flex: 1,
        width: "100%",
        maxWidth: WIDE_CONTENT_MAX,
        paddingHorizontal: 40,
    },
    wideScrollContent: {
        paddingTop: 48,
        paddingBottom: 28,
    },
})

const mapStateToProps = (state) => ({
    reduxFunnelStateId: state.data?.summerFunnel?.funnelStateId ?? null,
    reduxInitUser: state.data?.summerFunnel?.initUser ?? null,
})

const mapDispatchToProps = { apiRequest }

export default connect(mapStateToProps, mapDispatchToProps)(SummerClassScreen)
